import { createContext, useContext, useState, ReactNode, useEffect } from 'react';

interface SidebarContextType {
  isCollapsed: boolean;
  toggleSidebar: () => void;
  setCollapsed: (collapsed: boolean) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

interface SidebarProviderProps {
  children: ReactNode;
}

export const SidebarProvider = ({ children }: SidebarProviderProps) => {
  // Default to collapsed on mobile and smaller screens, expanded only on larger screens
  const [isCollapsed, setIsCollapsed] = useState(true);

  // Check screen size and set appropriate default state
  useEffect(() => {
    const SIDEBAR_STORAGE_KEY = 'musterix-sidebar-collapsed';
    
    const initializeSidebar = () => {
      try {
        // Try to get saved state from localStorage
        const savedState = localStorage.getItem(SIDEBAR_STORAGE_KEY);
        if (savedState !== null) {
          setIsCollapsed(JSON.parse(savedState));
        } else {
          // No saved state, use screen size based default
          const isLargeScreen = window.innerWidth >= 1280; // xl breakpoint
          setIsCollapsed(!isLargeScreen);
        }
      } catch (error) {
        // Fallback to screen size based default if localStorage is not available
        const isLargeScreen = window.innerWidth >= 1280;
        setIsCollapsed(!isLargeScreen);
      }
    };

    // Set initial state
    initializeSidebar();

    // Update on resize but preserve user preference
    const handleResize = () => {
      // On mobile, force collapsed
      if (window.innerWidth < 768) { // md breakpoint
        setIsCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    
    // Persist state to localStorage
    try {
      localStorage.setItem('musterix-sidebar-collapsed', JSON.stringify(newState));
    } catch (error) {
      // Silently fail if localStorage is not available
      console.warn('Failed to save sidebar state:', error);
    }
  };

  const setCollapsed = (collapsed: boolean) => {
    setIsCollapsed(collapsed);
  };

  return (
    <SidebarContext.Provider value={{ isCollapsed, toggleSidebar, setCollapsed }}>
      {children}
    </SidebarContext.Provider>
  );
};